<template>
  <div class="container">
    <div class="card shadow-lg p-4">

      <!-- Editable Header -->
      <section class="mb-5 mt-3 text-center">
        <div v-if="!isEditingHeader">
          <h5 class="mb-3">{{ headerText }}</h5>
          <button class="btn btn-outline-primary rounded-pill px-4" @click="isEditingHeader = true">
            ✏️ Edit
          </button>
        </div>
        <div v-else>
          <input v-model="editableHeader" class="form-control text-center mx-auto mb-3" style="max-width: 500px;" />
          <button class="btn btn-success rounded-pill px-4" @click="saveHeader">
            💾 Save Header
          </button>
        </div>
      </section>

      <hr />

      <!-- Image Upload and Preview -->
      <section class="mb-5 mt-3 text-center">
        <h4 class="mb-3">PDPA Images</h4>

        <CRow class="justify-content-center g-4">
          <CCol v-for="(image, index) in images" :key="index" cols="12" md="8">
            <div class="position-relative">
              <img
                  :src="getFullImagePath(image)"
                  alt="Uploaded Image"
                  class="d-block mx-auto rounded shadow"
                  style="max-height: 300px; object-fit: contain; width: 100%;"
              />
              <button
                  class="btn btn-sm btn-danger position-absolute"
                  style="top: 10px; right: 10px;"
                  @click="removeImage(image)"
              >
                ❌
              </button>
            </div>
          </CCol>
        </CRow>

        <div class="text-center mt-5">
          <label class="btn btn-outline-secondary rounded-pill px-4">
            📁 Choose Images
            <input type="file" multiple @change="handleImageUpload" accept="image/*" hidden />
          </label>
        </div>
      </section>

      <hr />

      <!-- Checkbox Label Editor -->
      <section class="mb-5 mt-3 text-center">
        <h4 class="mb-3">Desciption Checkbox Label</h4>
        <div v-if="isEditingRatio">
          <input v-model="editableRadioLabel" class="form-control text-center mx-auto mb-3" style="max-width: 500px;" />
          <button class="btn btn-success rounded-pill px-4" @click="saveRatioText">
            💾 Save Label
          </button>
        </div>
        <div v-else>
          <p class="mb-3">{{ radioLabel }}</p>
          <button class="btn btn-outline-primary rounded-pill px-4" @click="isEditingRatio = true">
            ✏️ Edit Label
          </button>
        </div>
      </section>

      <hr />

      <!-- Swal Message Editor -->
      <section class="mb-5 mt-3 text-center">
        <h4 class="mb-3">PDPA Message</h4>
        <div v-if="isEditingSwal">
          <textarea v-model="editableSwalMessage" class="form-control mx-auto mb-3 text-center" style="max-width: 800px;" rows="6" />
          <button class="btn btn-success rounded-pill px-4" @click="saveSwalMessage">
            💾 Save Message
          </button>
        </div>
        <div v-else>
          <p class="mb-3">{{ swalMessage }}</p>
          <button class="btn btn-outline-primary rounded-pill px-4" @click="isEditingSwal = true">
            ✏️ Edit Message
          </button>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2'
import api from '@/service/api'

export default {
  name: 'PdpaConsent',
  data() {
    return {
      pdpaId: '',
      headerText: '',
      editableHeader: '',
      isEditingHeader: false,

      radioLabel: '',
      editableRadioLabel: '',
      isEditingRatio: false,

      swalMessage: '',
      editableSwalMessage: '',
      isEditingSwal: false,

      images: []
    }
  },

  mounted() {
    this.loadData()
  },

  methods: {
    async loadData() {
      try {
        const res = await api.pdpa('get')
        const data = res.data.data?.[0]
        if (data) {
          this.pdpaId = data._id
          this.headerText = data.pdpa_header
          this.editableHeader = data.pdpa_header
          this.radioLabel = data.redio_label
          this.editableRadioLabel = data.redio_label
          this.swalMessage = data.massage
          this.editableSwalMessage = data.massage
          if (data.pdpa_img) this.images = data.pdpa_img
        }
      } catch (err) {
        console.error(err)
        Swal.fire('Error', 'Failed to load PDPA data.', 'error')
      }
    },

    getFullImagePath(imagePath) {
      return `https://election-su.mfu.ac.th${imagePath}`
    },

    async handleImageUpload(event) {
      const files = event.target.files;
      if (!files.length) return;

      const formData = new FormData();
      const validImageTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

      for (const file of files) {
        if (!validImageTypes.includes(file.type)) {
          Swal.fire({
            icon: 'error',
            title: 'Invalid File Type',
            text: 'Only image files are allowed (jpg, png, gif, webp).',
            confirmButtonText: 'OK'
          });
          event.target.value = ''; // reset input
          return;
        }

        formData.append('images', file);
      }

      try {
        await api.pdpa('addPictures', { id: this.pdpaId, data: formData });
        Swal.fire('Success', 'Images uploaded successfully.', 'success');
        this.loadData();
      } catch (err) {
        console.error(err);
        Swal.fire('Error', 'Failed to upload images.', 'error');
      }
    },

    async removeImage(image) {
      try {
        await api.pdpa('removePicture', { id: this.pdpaId, image })
        Swal.fire('Deleted', 'Image removed.', 'success')
        this.loadData()
      } catch (err) {
        console.error(err)
        Swal.fire('Error', 'Failed to remove image.', 'error')
      }
    },

    async saveHeader() {
      try {
        await api.pdpa('put', { _id: this.pdpaId, pdpa_header: this.editableHeader })
        this.headerText = this.editableHeader
        this.isEditingHeader = false
        Swal.fire('Saved', 'Header updated successfully.', 'success')
      } catch (err) {
        console.error(err)
        Swal.fire('Error', 'Failed to update header.', 'error')
      }
    },

    async saveRatioText() {
      try {
        await api.pdpa('put', { _id: this.pdpaId, redio_label: this.editableRadioLabel })
        this.radioLabel = this.editableRadioLabel
        this.isEditingRatio = false
        Swal.fire('Saved', 'Label updated successfully.', 'success')
      } catch (err) {
        console.error(err)
        Swal.fire('Error', 'Failed to update label.', 'error')
      }
    },

    async saveSwalMessage() {
      try {
        await api.pdpa('put', { _id: this.pdpaId, massage: this.editableSwalMessage })
        this.swalMessage = this.editableSwalMessage
        this.isEditingSwal = false
        Swal.fire('Saved', 'Message updated successfully.', 'success')
      } catch (err) {
        console.error(err)
        Swal.fire('Error', 'Failed to update message.', 'error')
      }
    }
  }
}
</script>

<style scoped>
button {
  transition: all 0.3s ease;
}
button:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
}
</style>
